import { DiscussionContentItem } from '@front-app/services/GroupsApi/DiscussionModel';
import { GroupDiscussionFollowAction, toggleGroupDiscussionFollowStatus } from '@front-app/services/GroupsApi/GroupsDiscussionApi';
import { CurrentUserStore } from '@front-app/store/singletons/User';
import { computed, Ref, ref } from '@vue/composition-api';

export interface UseDiscussionFollow {
	// True if the current user is following the discussion in LOCAL STATE
	isFollowing: Ref<boolean>;
	// True if we are currently making a request to modify the value of `isFollowing` to `!isFollowing`
	isSubmittingFollow: Ref<boolean>;
	// Make a request to modify the value of `isFollowing` to `!isFollowing` IN BACKEND STATE. This does not affect local state; ie:
	// `isFollowing` will not change after this call
	submitToggleFollow(): Promise<void>;
	// Apply a toggle to the local state, will actually trigger the local state change to cause `isFollowing` to
	// flip between true/false
	toggleLocalFollowState(): void;
}

/**
 * @deprecated in favour of useDiscussionStore
 */
export const useDiscussionFollow = (content: Ref<DiscussionContentItem | null>): UseDiscussionFollow => {
	const isSubmittingFollow = ref(false);
	const isFollowing = computed(() => {
		const currentUserId = CurrentUserStore.currentUser.value.id;
		const currentUserAsFollower = content.value?.metadata.discussion
			?.followers
			.find(({ id }) => id === currentUserId);
		return Boolean(currentUserAsFollower);
	});
	const submitToggleFollow = async () => {
		if (!content.value) {
			return Promise.reject();
		}
		const action = isFollowing.value
			? GroupDiscussionFollowAction.UNFOLLOW
			: GroupDiscussionFollowAction.FOLLOW;
		isSubmittingFollow.value = true;
		try {
			await toggleGroupDiscussionFollowStatus(content.value.id, action);
			isSubmittingFollow.value = false;
			return Promise.resolve();
		} catch (e) {
			isSubmittingFollow.value = false;
			return Promise.reject();
		}
	};

	const toggleLocalFollowStateInternal = () => toggleLocalFollowState(content.value, isFollowing.value);

	return {
		isFollowing,
		isSubmittingFollow,
		submitToggleFollow,
		toggleLocalFollowState: toggleLocalFollowStateInternal,
	};
};

export function toggleLocalFollowState(content: DiscussionContentItem | null, isFollowing: boolean): void {
	if (!content) {
		// eslint-disable-next-line no-console
		console.error('Could not apply follow change to non-existant discussion');
		return;
	}
	if (isFollowing) {
		setUnfollowed(content);
	} else {
		setFollowed(content);
	}
}

function setFollowed(content: DiscussionContentItem) {
	// Add current user to the followers
	const { discussion } = content.metadata;
	discussion.followers = [...discussion.followers, CurrentUserStore.currentUser.value];
}

function setUnfollowed(content: DiscussionContentItem) {
	// Remove current user from the followers
	const { discussion } = content.metadata;
	const currentUserId = CurrentUserStore.currentUser.value.id;
	discussion.followers = discussion.followers.filter(({ id }) => id !== currentUserId);
}
